"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-3 py-2 text-foreground hover:text-primary hover:bg-transparent focus:bg-transparent text-[12px] font-sans font-bold tracking-widest transition-colors">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-[1px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-t border-border last:border-b">
          <Link to={item.href || "#"} onClick={onNavigate} className="flex items-baseline gap-4 py-6 text-foreground hover:text-primary transition-colors">
            <span className="font-mono text-xs text-muted-foreground tracking-wider">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="font-default text-[2.5rem] font-bold leading-none tracking-tighter">
              {item.label}
            </span>
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4560" id="κεντρική-πλοήγηση" className={`sticky top-0 z-50 w-full bg-background text-foreground border-b transition-all duration-300 ${scrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "border-border"}`}>
      <div className="w-full px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-2 md:grid-cols-3 items-center h-16 md:h-20">
          {}
          <div className="flex items-center justify-start">
            <Link to="/" className="flex items-center mr-8" aria-label="HotelDeals.gr home">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/132EAB99-18DA-419F-ACE9-F2E102428C02.png" className="h-12 w-auto" alt="HotelDeals.gr" loading="lazy" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/132EAB99-18DA-419F-ACE9-F2E102428C02.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/132EAB99-18DA-419F-ACE9-F2E102428C02.png-wvc-sizes" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex items-center justify-center">
            <MenuProvider menu_id="47">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center justify-end gap-3">
            <div className="flex items-center">
              <Button asChild variant="outline" className="h-10 px-6 bg-[#82EE09] border-none hover:bg-[#82EE09]/90 text-white font-mono text-[11px] font-bold tracking-[0.15em] gap-2 rounded-md shadow-md">
                <Link to="https://hoteldeals.vlmtravel.com/#/welcome" newTab>
                  <span className="relative flex items-center justify-center">
                    <span className="absolute inline-flex h-2 w-2 rounded-md bg-white opacity-75 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-md bg-white" />
                  </span>
                  <span>Αναζήτηση</span>
                </Link>
              </Button>
            </div>

            {}
            <div className="md:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent rounded-md" aria-label="Open menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background text-foreground border-l border-border w-full sm:max-w-full p-6 sm:p-8 overflow-y-auto">
                  <SheetTitle className="sr-only">Πλοήγηση</SheetTitle>

                  <div className="flex items-center justify-between pt-1">
                    <Link to="/" onClick={() => setMobileOpen(false)} aria-label="HotelDeals.gr home">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/132EAB99-18DA-419F-ACE9-F2E102428C02.png" className="h-12 w-auto" alt="HotelDeals.gr" loading="lazy" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/132EAB99-18DA-419F-ACE9-F2E102428C02.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/132EAB99-18DA-419F-ACE9-F2E102428C02.png-wvc-sizes" />
                    </Link>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Μενού
                    </span>
                  </div>

                  <MenuProvider menu_id="47">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-10">
                    <Button asChild variant="outline" className="w-full h-14 bg-accent border-none hover:bg-accent/90 text-accent-foreground font-mono text-[12px] font-bold tracking-[0.15em] gap-2 rounded-md" onClick={() => setMobileOpen(false)}>
                      <Link to="https://hoteldeals.vlmtravel.com/#/welcome" newTab>
                        <span className="relative flex items-center justify-center">
                          <span className="absolute inline-flex h-2 w-2 rounded-md bg-white opacity-75 animate-ping" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-md bg-white" />
                        </span>
                        <span>Αναζήτηση Προσφορών</span>
                      </Link>
                    </Button>
                  </div>

                  <div className="mt-10 pt-6 border-t border-border">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      VLM Travel | Αθήνα
                    </p>
                    <p className="mt-2 font-default text-sm text-foreground/80">
                      support@hoteldeals.gr
                    </p>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}